const ICON_BAG = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" aria-hidden="true"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16.875 5.625H3.125a.625.625 0 0 0-.625.625v9.375c0 .345.28.625.625.625h13.75c.345 0 .625-.28.625-.625V6.25a.625.625 0 0 0-.625-.625"/><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M6.875 7.5V5a3.125 3.125 0 0 1 6.25 0v2.5"/></svg>`
const ICON_LOADING = `<svg aria-hidden="true" focusable="false" role="presentation" class="ico-Spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="ico-SpinnerPath" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle></svg>`
const ICON_WISHLIST = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" aria-hidden="true"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3.172 4.172a4 4 0 0 1 5.656 0L10 5.343l1.172-1.17a4 4 0 1 1 5.656 5.655L10 16.658l-6.828-6.83a4 4 0 0 1 0-5.656Z" clip-rule="evenodd"/></svg>`

/**
 * Product card renderer.
 * @param {number} index - Product card index.
 * @param {object} product - Product object from Nosto.
 * @param {string} type - Nosto type, either 'autocomplete', 'category', 'serp'
 * or 'recommendation'.
 */
function ProductCard ({ index, product, type = 'category' } = {}) {
  if (!product) {
    return ''
  }

  const sizes =
    type === 'results' || type === 'serp'
      ? '(max-width: 767px) 50vw, 25vw'
      : '(max-width: 767px) 50vw, 500px'

  const hoverImageUrl =
    (product.alternateImageUrls && product.alternateImageUrls[0]) || ''

  const formattedFields = formatCustomFields(product.customFields)

  let url = ''

  if (product.url) {
    url = new URL(product.url)
    url = url.pathname

    if (!url.startsWith(window.routes.rootUrl)) {
      url = `${window.routes.rootUrl}${url}`
    }
  }

  const props = {
    available: true,
    badge: formattedFields.badge,
    colourName: formattedFields.colourName,
    compareAtPrice: product.listPrice || false,
    featuredImage: {
      alt: product.name || '',
      height: 2620,
      sizes,
      src: product.imageUrl,
      srcset: buildShopifySrcset(product.imageUrl),
      width: 2100,
    },
    handle: product.url?.split('/')?.pop() || '',
    hoverImage: {
      alt: product.name || '',
      height: 2620,
      sizes,
      src: hoverImageUrl,
      srcset: buildShopifySrcset(hoverImageUrl),
      width: 2100,
    },
    id: product.productId || '',
    material: formattedFields.material,
    price: product.price || '',
    tags: product.tags1 || [],
    title: product.name || '',
    url,
  }

  let accessibilityTitle = props.title

  if (props.colourName) {
    accessibilityTitle += ` - ${props.colourName}`
  }

  const productAriaLabel =
    window.theme.strings.accessibility.navigateTo.replaceAll(
      '[title]',
      accessibilityTitle,
    )

  const quickAddAriaLabel =
    window.theme.strings.product.quickAddOpen.replaceAll(
      '[title]',
      accessibilityTitle,
    )

  return `
    <product-card
      class="prd-Card util-FauxLink"
      data-colours-loaded="false"
      data-nosto-type="${type}"
      data-product-id="${props.id}"
      data-product-url="${props.url}"
    >
      <div class="prd-Card_Link">
        <a
          class="util-FauxLink_Link"
          href="${props.url}"
          aria-label="${productAriaLabel}"
        ></a>

        <div class="prd-Card_MediaContainer">
          <div class="prd-Card_Images${props.hoverImage.src ? ' prd-Card_Images-hasHover' : ''}">
            ${renderImage({
              image: props.featuredImage,
              index,
              label: productAriaLabel,
              url: props.url,
            })}

            ${renderImage({
              hoverImage: true,
              image: props.hoverImage,
              index,
              label: productAriaLabel,
              url: props.url,
            })}
          </div>

          ${renderBadge(props.badge)}

          <div class="prd-Card_Buttons">
            ${renderWishlist(props)}

            <button
              class="prd-Card_Button"
              type="button"
              data-product-url="${props.url}"
              data-quick-add-external-el="trigger"
              aria-haspopup="dialog"
              aria-label="${quickAddAriaLabel}"
            >
              <span class="prd-Card_ButtonIcon">
                ${ICON_BAG}
              </span>

              <span class="prd-Card_ButtonIcon prd-Card_ButtonIcon-active">
                ${ICON_LOADING}
              </span>
            </button>
          </div>
        </div>

        <div class="prd-Card_Body">
          <${type === 'results' ? 'h3' : 'p'}
            id="ProductCard-${props.id}"
            class="prd-Card_Title fz-13_100"
          >
            ${props.title}
            ${renderColourName(props.colourName)}
          </${type === 'results' ? 'h3' : 'p'}>

          <div class="prd-Card_BodyInner">
            ${renderMaterial(props.material)}

            <div class="prd-Card_Price fz-14_120">
              ${renderPrice(props)}
            </div>
          </div>
        </div>
      </div>

      ${renderColours(props.colourName)}
    </product-card>
  `
}

function formatCustomFields (customFields) {
  if (!customFields || !customFields.length) return {}

  const colour =
    customFields.find((field) => {
      return field.key === 'badge-colour'
    })?.value || '#fff'

  const text =
    customFields.find((field) => {
      return field.key === 'badge-text'
    })?.value || ''

  const textInvert =
    customFields.find((field) => {
      return field.key === 'badge-text_invert'
    })?.value || false

  const colourName =
    customFields.find((field) => {
      return field.key === 'colour-name'
    })?.value || ''

  const material =
    customFields.find((field) => {
      return field.key === 'details-material'
    })?.value || ''

  return {
    badge: {
      colour,
      text,
      textInvert: textInvert === 'true',
    },
    colourName,
    material,
  }
}

function buildShopifySrcset (src) {
  if (!src) return ''

  try {
    const baseUrl = new URL(src, window.location.origin)
    if (!baseUrl.hostname.includes('cdn.shopify.com')) return ''

    return [
      180, 360, 540, 620, 750, 810, 900, 1080, 1296, 1380, 1512, 1728, 1944,
      2100,
    ]
      .map((width) => {
        return `${window.BAO.Utils.resizeImage({ src, width })} ${width}w`
      })
      .join(', ')
  } catch {
    return ''
  }
}

function renderImage ({ hoverImage, image, index, label, url } = {}) {
  if (!image || !image.src) return ''

  const aspectRatio = Math.round((image.height / image.width) * 100)
  const loading = index > 7 || hoverImage ? 'lazy' : 'eager'
  const fetchpriority = index === 0 && !hoverImage ? 'high' : 'auto'

  return `
    <a
      class="prd-Card_Image${hoverImage ? ' prd-Card_Image-hover' : ''}"
      href="${url}"
      tabindex="-1"
      aria-label="${label}"
      aria-hidden="true"
    >
      <div
        class="rsp-Image"
        style="
          --Image_AspectRatio: ${aspectRatio}%;
          --Aspect_Ratio: ${image.width} / ${image.height};
        "
      >
        <img
          class="rsp-Image_Image"
          alt="${image.alt}"
          height="${image.height}"
          loading="${loading}"
          fetchpriority="${fetchpriority}"
          sizes="${image.sizes}"
          src="${window.BAO.Utils.resizeImage(image)}"
          srcset="${image.srcset}"
          width="${image.width}"
        >
      </div>
    </a>
  `
}

function renderBadge (badge) {
  if (!badge || !badge.text) return ''

  const textColour = badge.textInvert
    ? 'var(--Color_White)'
    : 'var(--Color_Brand)'

  return `
    <span
      class="prd-Card_Badge fz-11_100"
      style="
        --Background: ${badge.colour};
        --Text: ${textColour};
      "
    >
      ${badge.text}
    </span>
  `
}

function renderWishlist (props) {
  if (!window.theme.settings.showWishlist) return ''

  return `
    <button
      class="prd-Card_Button prd-Card_Button-wishlist"
      type="button"
      data-wishlist-modal-external-el="trigger"
      data-product-title="${props.title}"
      data-product-material="${props.material}"
      data-product-image="${props.featuredImage.src}"
      data-product-id="${props.id}"
      data-variant-id=""
      data-product-url="${window.theme.shop.url}${props.url}"
      data-variant-url=""
      aria-label=${window.theme.strings.product.wishlistAdd}"
    >
      ${ICON_WISHLIST}
    </button>
  `
}

function renderColourName (colourName) {
  if (!colourName) return ''

  return `
    <span class="util-ScreenReaderOnly"> - ${colourName}</span>
  `
}

function renderMaterial (material) {
  if (!material) return ''

  return `
    <p class="prd-Card_Text fz-14_120">
      ${material}
    </p>
  `
}

function renderPrice ({ compareAtPrice, price } = {}) {
  const compareAtPriceAmount = window.BAO.Utils.formatMoney(
    compareAtPrice * 100,
    window.theme.shop.moneyFormat,
  )

  const priceAmount = window.BAO.Utils.formatMoney(
    price * 100,
    window.theme.shop.moneyFormat,
  )

  if (compareAtPrice > price) {
    const percent = Math.round(100 - price / (compareAtPrice / 100))

    const percentString = window.theme.strings.product.percentOff.replace(
      '[percent]',
      percent,
    )

    return `
      <span class="prd-Price">
        <span class="prd-Price_SalePrice">
          <span class="util-ScreenReaderOnly">
            ${window.theme.strings.product.salePrice}
          </span>

          ${priceAmount}
        </span>

        <s class="prd-Price_RegularPrice">
          <span class="util-ScreenReaderOnly">
            ${window.theme.strings.product.regularPrice}
          </span>

          ${compareAtPriceAmount}
        </s>

        <span class="prd-Price_PercentOff">
          ${percentString}
        </span>
      </span>
    `
  }

  return `
    <span class="prd-Price">
      ${priceAmount}
    </span>
  `
}

function renderColours (colourName) {
  if (!colourName) return ''

  return `
    <div
      class="prd-Card_Colours"
      data-product-card-el="colours"
    ></div>
  `
}

if (window.templates) {
  window.templates.ProductCard = ProductCard
}
